import {
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import {
    Outlet,
    useNavigation,
} from 'react-router-dom';
import { AlertContainer } from '@ifrc-go/ui';
import {
    type Language,
    LanguageContext,
} from '@ifrc-go/ui/contexts';
import {
    _cs,
    listToGroupList,
    listToMap,
    mapToMap,
} from '@togglecorp/fujs';
import { type } from 'arktype';

import GlobalFooter from '#components/GlobalFooter';
import Navbar from '#components/Navbar';
import HealthCheckContext, { HealthCheckData } from '#contexts/HealthCheckContext';
import useDebouncedValue from '#hooks/useDebouncedValue';
import { resolveUrl } from '#utils/resolveUrl';

import styles from './styles.module.css';

// eslint-disable-next-line import/prefer-default-export
export function Component() {
    const { state } = useNavigation();
    const isLoading = state === 'loading';
    const isLoadingDebounced = useDebouncedValue(isLoading);
    const [languagePending, setLanguagePending] = useState(false);

    const [healthCheckData, setHealthCheckData] = useState<HealthCheckData>();

    const {
        currentLanguage,
        setStrings,
    } = useContext(LanguageContext);

    useEffect(() => {
        async function healthCheck() {
            try {
                const res = await fetch(
                    resolveUrl(import.meta.env.APP_API_DOMAIN, 'health-check/?format=json'),
                    {
                        method: 'GET',
                        credentials: 'include',
                    },
                );
                const serverResponse = await res.json();

                const healthData = type.object.as<HealthCheckData>()(serverResponse);

                if (healthData instanceof type.errors) {
                    // eslint-disable-next-line no-console
                    console.error(healthData.summary);
                } else {
                    setHealthCheckData(healthData);
                }
            } catch (ex) {
                // eslint-disable-next-line no-console
                console.error('Error getting health check', ex);
            }
        }
        healthCheck();
    }, []);

    const fetchLanguage = useCallback(async (lang: Language) => {
        setLanguagePending(true);
        const resource = await import(`./translations/${lang}.json`);
        const stringList = resource.default as {
            key: string;
            namespace: string;
            value: string;
        }[];

        setStrings((oldValue) => ({
            ...oldValue,
            ...mapToMap(
                listToGroupList(
                    stringList,
                    ({ namespace }) => namespace,
                ),
                (key) => key,
                (values, k) => ({
                    ...oldValue[k],
                    ...listToMap(
                        values,
                        ({ key }) => key,
                        ({ value }) => value,
                    ),
                }),
            ),
        }));
        setLanguagePending(false);
    }, [setStrings]);

    useEffect(
        () => {
            if (
                languagePending || currentLanguage === 'en'
            ) {
                return;
            }

            fetchLanguage(currentLanguage);
        },
        [
            currentLanguage,
            languagePending,
            fetchLanguage,
        ],
    );

    return (
        <HealthCheckContext.Provider value={healthCheckData}>
            <div className={styles.root}>
                {(isLoading || isLoadingDebounced) && (
                    <div
                        className={_cs(
                            styles.navigationLoader,
                            !isLoading && isLoadingDebounced && styles.disappear,
                        )}
                    />
                )}
                <Navbar className={styles.navbar} />
                <div className={styles.pageContent}>
                    <Outlet />
                </div>
                <GlobalFooter
                    className={styles.footer}
                />
                <AlertContainer />
            </div>
        </HealthCheckContext.Provider>
    );
}

Component.displayName = 'Root';
